export const informationExtractionDocumentationMarkdown = `
# Information Extraction

Extract structured information from unstructured text using LLM and extraction configurations.

## Configuration

- Documents: You can set this input as \`{{previousBlock.data}}\` which can have an array of documents format \`[{
  "text": "document text",
  … any additional metadata can be added here
}]\` or a single text format "document text". Typically this field refers to a list of patient documents, e.g., output of patient data store block.
- Extraction fields: Array of entities to extract, each with a name and description. Name can be any identifier to define the field. Description is guiding language model as to what and how data will be extracted. See the example below, where name is "birth_year" and description is "Year of birth in YYYY format". In this case, language model is instructed to read the document text and extract year of birth in desired format and store resulting extraction in the field referred to as "birth_year".
- Examples (optional): Array of input/output examples showing desired extraction cases. This section is especially useful if you want to cover edge cases and instruct language model the desired behaviour. For example, if document states "Mr. John Doe", and if "name" field is to be extracted with description "patient's first and last name" produces "Mr. John Doe" as the extracted value, you can fix such issues by updating description field or by explicitly providing an example. In this case, you can provide an example, where "Dr. John Doe" is input and "John Doe" is output. You can add more examples to cover such edge cases in your workflow.
- Prompt for extraction guidance (optional): Extra prompt text to guide extraction. You can add any additional instructions here, e.g., "only extract verbatim quotes from document, do not interpret results".
- Prompt for ranking and filtering results (optional): Prompt text for ranking/filtering results. See example below.
- Model (optional): Choose from available LLM models like Gemini, Claude, GPT-4, etc. Each has different capabilities and pricing.

## Output

- Output has extractions field with extracted information for each document based on the configuration.
- If a field is defined as birth_year (see example below), then you can access to this output for the first document with \`{{informationExtraction.data.extractions[0].birth_year}}\` in subsequent blocks for further processing. In the example below, this refers to YYYY formatted year, e.g., "1995". 
- When input is an array of documents and if any document does not have desired "text" field, that document will be skipped and can be viewed in the output skippedDocuments.


## Example

### Medical Records Information Extraction

This example shows how to extract patient information from multiple documents.

#### Documents
~~~javascript
[
  {
    "text": "Patient presents as Emily Thompson, date of birth March 15, 1988. Chief complaint: headache",
    "id": "001",
    "title": "Initial Visit Note",
    "date": "2024-01-15"
  },
  {
    "text": "Follow-up with patient Mr. Michael Rodriguez, DOB: 12/21/1995. Blood pressure normal.",
    "id": "002",
    "title": "Progress Note",
    "date": "2024-02-01"
  },
  {
    "text": "Patient Sarah Wilson, born on July 8, 1979, reports improvement in symptoms.",
    "id": "003",
    "title": "Progress Note",
    "date": "2024-01-20"
  },
  {
    "text": "Patient Mark Wilson, born on July 8, 1969.",
    "id": "004",
    "title": "Progress Note",
    "date": "2023-01-20"
  }
]
~~~

#### Extraction fields

| Name | Description |
|------------|-------------|
| name | Full name of the patient (first and last name) |
| birth_year | Year of birth in YYYY format |

#### Examples

| Input | Output |
|-------|--------|
| Mr. John Doe, dob: Jan 1, 2004 | {"name": "John Doe", "birth_year": "2004"} |


#### Prompt for extraction guidance
~~~
- Ignore middle names or titles (Dr., Mr., etc.)
- Return None if a field cannot be confidently extracted
~~~

#### Prompt for ranking and filtering results
~~~
- Filter documents to include only those with "Progress Note" in metadata
- Sort filtered documents by date in descending order (newest first)
- Return the information extraction from the top 2 most recent progress notes
~~~

#### Output

The output from block includes information extracted from the two most recent "Progress Note" documents.

~~~javascript
[
  {
    "id": "002",
    "name": "Michael Rodriguez",
    "birth_year": "1995"
  },
  {
    "id": "003",
    "name": "Sarah Wilson",
    "birth_year": "1979"
  },
]
~~~

`;
